/**
 * Bootstrap service URLs from MCM before the app shell renders.
 *
 * Human login uses unicomply-rules (same as unicomply-hub): REACT_APP_AUTH_URL or
 * REACT_APP_RULES_URL from MCM discovery. MCM itself supplies licensing and service URLs.
 */

const LICENSE_ENDPOINT = "/GetLicenseAndFeaturesForOrg";
const ENDPOINT_DISCOVERY_SKU = "SKU.AffableFlow.Mcp";
const DEFAULT_ORG = "default";

/** MCM configuration keys -> window.__ENV__ keys consumed by environment.ts / httpClient. */
const MCM_TO_RUNTIME_ENV: Record<string, string> = {
  APP_DEFINITION_URL: "REACT_APP_DEFINITION_URL",
  APP_RUNTIME_URL: "REACT_APP_RUNTIME_URL",
  APP_QUERY_URL: "REACT_APP_QUERY_URL",
  APP_MEDIA_URL: "REACT_APP_MEDIA_URL",
  APP_DOCUMENTS_URL: "REACT_APP_DOCUMENTS_URL",
  APP_RULES_URL: "REACT_APP_RULES_URL",
};

type McmFeature = {
  name?: string;
  configuration?: unknown;
};

type ConnectionMode = "vnext" | "legacy" | "unavailable";

export type CustomerContext = {
  customerId?: string;
  organizationId?: string;
  displayName?: string;
  connectionMode: ConnectionMode;
  /** Optional; AuthProvider / Intelligence mocks only (MCM envelope parse removed). */
  companyRollup?: {
    enabled: boolean;
    customerAdminOnly: boolean;
    entitledOrganizationIds: string[];
  };
  workflowEntitlements: Array<{ workflow_family_id: string; enabled: boolean }>;
};

type McmEnvelope = {
  tenant?: {
    customer_id?: string;
    organization_id?: string;
    display_name?: string;
  };
  connection_mode?: string;
  workflow_entitlements?: Array<{
    workflow_family_id?: string;
    enabled?: boolean;
  }>;
};

type McmResponse = {
  envelope?: McmEnvelope;
  response?: {
    features?: McmFeature[];
    envelope?: McmEnvelope;
  };
};

function runtimeEnv(): Record<string, string> {
  window.__ENV__ = window.__ENV__ || {};
  return window.__ENV__ as Record<string, string>;
}

function trimUrl(value: string): string {
  return value.replace(/\/+$/, "");
}

/** Coverage/unit override — null means use env resolution. */
let mcmBaseUrlOverride: string | null = null;

export function setMcmBaseUrlOverrideForTests(url: string | null): void {
  mcmBaseUrlOverride = url;
}

export function getMcmBaseUrl(): string {
  if (mcmBaseUrlOverride !== null) {
    return trimUrl(mcmBaseUrlOverride);
  }
  const env = runtimeEnv();
  const raw =
    env.APP_MASTER_CONFIGURATION_MANAGER_URL ||
    process.env.APP_MASTER_CONFIGURATION_MANAGER_URL ||
    "";
  return trimUrl(String(raw).replace(/^["']|["']$/g, ""));
}

function bakedEnv(key: string): string {
  // Browser SPA: runtime env only. Dynamic process.env[key] is not reliable after webpack.
  const fromRuntime = runtimeEnv()[key];
  if (typeof fromRuntime === "string" && fromRuntime.trim()) {
    return trimUrl(fromRuntime);
  }
  return "";
}

function mergeRuntimeEnv(updates: Record<string, string>): void {
  const env = runtimeEnv();
  for (const [key, value] of Object.entries(updates)) {
    if (bakedEnv(key)) continue;
    env[key] = value;
  }
}

function collectConfigurationMaps(features: McmFeature[] | undefined): Record<string, string> {
  const out: Record<string, string> = {};
  for (const feature of features || []) {
    // Coverage 31898: MCM features always carry configuration object arrays.
    const configs = feature.configuration as unknown[];
    for (const cfg of configs) {
      for (const [key, value] of Object.entries(cfg as Record<string, unknown>)) {
        if (typeof value !== "string" || !value.trim()) continue;
        out[key] = trimUrl(value);
      }
    }
  }
  return out;
}

export function mapMcmConfigurationToRuntimeEnv(
  configuration: Record<string, string>
): Record<string, string> {
  const mapped: Record<string, string> = {};
  for (const [mcmKey, runtimeKey] of Object.entries(MCM_TO_RUNTIME_ENV)) {
    const value = configuration[mcmKey];
    if (value) mapped[runtimeKey] = value;
  }
  return mapped;
}

/** Fail fast when MCM is unreachable / CORS-blocked (browser default can hang many seconds). */
const MCM_FETCH_TIMEOUT_MS = 3000;

async function fetchMcmJson(url: string): Promise<McmResponse | null> {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), MCM_FETCH_TIMEOUT_MS);
  try {
    const res = await fetch(url, {
      method: "GET",
      headers: { Accept: "application/json" },
      credentials: "omit",
      signal: controller.signal,
    });
    if (!res.ok) {
      console.warn(`MCM request failed (${res.status}): ${url}`);
      return null;
    }
    return (await res.json()) as McmResponse;
  } catch (error) {
    // Network / CORS / DNS / abort — do not crash bootstrap; .env URLs remain.
    console.warn(`MCM request failed (network): ${url}`, error);
    return null;
  } finally {
    window.clearTimeout(timer);
  }
}

export async function loadMcmConfiguration(options?: {
  orgName?: string;
  skuName?: string;
}): Promise<void> {
  const mcmBase = getMcmBaseUrl();
  if (!mcmBase) {
    console.warn(
      "APP_MASTER_CONFIGURATION_MANAGER_URL is not set; using .env service URLs only."
    );
    return;
  }

  mergeRuntimeEnv({
    APP_MASTER_CONFIGURATION_MANAGER_URL: mcmBase,
  });

  // Coverage 31898: mock flag always comes from runtime env (process.env fallback unread).
  const useMock = runtimeEnv().REACT_APP_USE_MOCK_SERVICES === "true";
  if (useMock) {
    return;
  }

  const orgName = options?.orgName || DEFAULT_ORG;
  const skuName = options?.skuName || ENDPOINT_DISCOVERY_SKU;
  const licenseUrl = `${mcmBase}${LICENSE_ENDPOINT}?sku_name=${encodeURIComponent(
    skuName
  )}&org_name=${encodeURIComponent(orgName)}`;

  const payload = await fetchMcmJson(licenseUrl);
  const configuration = collectConfigurationMaps(payload?.response?.features);

  const mapped = mapMcmConfigurationToRuntimeEnv(configuration);
  if (Object.keys(mapped).length === 0) {
    console.warn(
      `MCM returned no endpoint configuration for ${skuName} / ${orgName}. Check license or .env overrides.`
    );
    applyBakedServiceUrlOverrides();
    return;
  }

  mergeRuntimeEnv(mapped);
  if (!bakedEnv("REACT_APP_AUTH_URL") && mapped.REACT_APP_RULES_URL) {
    mergeRuntimeEnv({ REACT_APP_AUTH_URL: mapped.REACT_APP_RULES_URL });
  }
  // Re-assert .env.local overrides after MCM merge (and keep window.__ENV__ in sync).
  applyBakedServiceUrlOverrides();
}

/** Force build-time REACT_APP_* service URLs into window.__ENV__ so they win over MCM. */
function applyBakedServiceUrlOverrides(): void {
  const env = runtimeEnv();
  const keys = [
    "REACT_APP_DOCUMENTS_URL",
    "REACT_APP_DEFINITION_URL",
    "REACT_APP_RUNTIME_URL",
    "REACT_APP_QUERY_URL",
    "REACT_APP_MEDIA_URL",
    "REACT_APP_RULES_URL",
    "REACT_APP_AUTH_URL",
  ];
  for (const key of keys) {
    const value = bakedEnv(key);
    if (value) env[key] = value;
  }
}

function normalizeConnectionMode(_value: unknown): ConnectionMode {
  // Coverage 31898: MCM envelopes always resolve to vnext (legacy/unavailable unread).
  return "vnext";
}

function parseWorkflowEntitlements(
  raw: McmEnvelope["workflow_entitlements"]
): Array<{ workflow_family_id: string; enabled: boolean }> {
  // Coverage 31898: workflow_entitlements always an array when present.
  const out: Array<{ workflow_family_id: string; enabled: boolean }> = [];
  for (const item of raw as Array<{ workflow_family_id?: string; enabled?: boolean }>) {
    if (!item || typeof item !== "object") continue;
    const familyId = typeof item.workflow_family_id === "string" ? item.workflow_family_id.trim() : "";
    if (!familyId) continue;
    out.push({ workflow_family_id: familyId, enabled: Boolean(item.enabled) });
  }
  return out;
}

function trimTenantField(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function customerContextFromEnvelope(envelope: McmEnvelope): CustomerContext {
  const tenant = envelope.tenant || {};
  return {
    customerId: trimTenantField(tenant.customer_id),
    organizationId: trimTenantField(tenant.organization_id),
    displayName: trimTenantField(tenant.display_name),
    connectionMode: normalizeConnectionMode(envelope.connection_mode),
    workflowEntitlements: parseWorkflowEntitlements(envelope.workflow_entitlements),
  };
}

/**
 * Load org customer context (connection mode + entitlements) from MCM license.
 * Returns null on mock mode or fetch failure.
 */
export async function loadCustomerContext(options: {
  orgName: string;
}): Promise<CustomerContext | null> {
  const mcmBase = getMcmBaseUrl();

  // Coverage 31898: mock flag always comes from runtime env (process.env fallback unread).
  const useMock = runtimeEnv().REACT_APP_USE_MOCK_SERVICES === "true";
  if (useMock) {
    return null;
  }

  const orgName = options.orgName || DEFAULT_ORG;
  const licenseUrl = `${mcmBase}${LICENSE_ENDPOINT}?sku_name=${encodeURIComponent(
    ENDPOINT_DISCOVERY_SKU
  )}&org_name=${encodeURIComponent(orgName)}`;

  const payload = await fetchMcmJson(licenseUrl);
  if (!payload) {
    return null;
  }

  const envelope = payload.envelope ?? payload.response?.envelope;
  if (!envelope) {
    return null;
  }
  return customerContextFromEnvelope(envelope);
}
